// The "Story" panel — rendered narrative at the top of the v3 drawer.

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Target } from "lucide-react";

import KillChainBar from "./KillChainBar";
import { actionLabel } from "../../../../../utils/wafEventV3";
import {
  spSumBodySx,
  spSumHeadlineSx,
  spObjectiveCalloutSx,
  spObjectiveIconSx,
  spObjectiveLabelSx,
  spObjectiveValueSx,
  spWhyOverlineSx,
  NumberedBadgeRow,
} from "./smartParseDrawerUi";

function escapeRegExp(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderSummaryWithBold(text, tokens = []) {
  if (!text) return null;

  const uniqueTokens = [...new Set(
    tokens.filter((t) => t != null && String(t).trim() !== "").map(String),
  )].sort((a, b) => b.length - a.length);

  if (uniqueTokens.length === 0) {
    return text;
  }

  const pattern = uniqueTokens.map(escapeRegExp).join("|");
  const parts = text.split(new RegExp(`(${pattern})`, "g"));

  return parts.map((part, i) => {
    if (uniqueTokens.includes(part)) {
      return <strong key={i}>{part}</strong>;
    }
    return part;
  });
}

function collectBoldTokens(event) {
  if (!event) return [];
  const { attacker, target } = event;
  return [
    attacker?.source_ip,
    target?.host,
    target?.uri,
    target?.path,
    target?.method,
    target?.response_status != null ? String(target.response_status) : null,
    target?.response_status != null ? `HTTP ${target.response_status}` : null,
  ];
}

export default function StoryPanel({
  story,
  assessment,
  event,
  embedded = false,
  hideKillChain = false,
  hideHeadline = false,
  headerSlot = null,
}) {
  if (!story) return null;

  const boldTokens = collectBoldTokens(event);
  const actionWord = assessment ? actionLabel(assessment.action).toLowerCase() : "blocked";

  const content = (
    <Stack sx={{ gap: "13px" }}>
      {!hideHeadline && story.headline && (
        <Typography sx={spSumHeadlineSx}>
          {story.headline}
        </Typography>
      )}

      {headerSlot}

      {story.summary && (
        <Typography sx={spSumBodySx}>
          {renderSummaryWithBold(story.summary, boldTokens)}
        </Typography>
      )}

      {story.attacker_objective && (
        <Box sx={spObjectiveCalloutSx}>
          <Box sx={spObjectiveIconSx}>
            <Target size={16} color="#B07D11" strokeWidth={2.5} aria-hidden />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={spObjectiveLabelSx}>
              Attacker&apos;s objective
            </Typography>
            <Typography sx={spObjectiveValueSx}>{story.attacker_objective}</Typography>
          </Box>
        </Box>
      )}

      {Array.isArray(story.why_we_blocked) && story.why_we_blocked.length > 0 && (
        <Box>
          <Typography sx={spWhyOverlineSx}>
            Why we {actionWord} this
          </Typography>
          <Stack sx={{ gap: "8px" }}>
            {story.why_we_blocked.map((reason, i) => (
              <NumberedBadgeRow key={i} index={i} text={reason} variant="why" />
            ))}
          </Stack>
        </Box>
      )}

      {Array.isArray(story.kill_chain) && story.kill_chain.length > 0 && !hideKillChain && (
        <Box>
          <Typography sx={spWhyOverlineSx}>Kill chain</Typography>
          <KillChainBar stages={story.kill_chain} />
        </Box>
      )}
    </Stack>
  );

  if (embedded) return content;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid rgba(55, 62, 120, 0.14)",
        backgroundColor: "rgba(40, 46, 100, 0.025)",
      }}
    >
      {content}
    </Box>
  );
}
